import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

export default function Team() {
  const t = useT();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState(""); const [role, setRole] = useState("member");

  const load = async () => {
    const m = await api.get("/team/members"); setMembers(m.data.members);
    try { const i = await api.get("/team/invites"); setInvites(i.data.invites); } catch {}
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/team/invite", { email, role });
      toast.success("Invite created");
      navigator.clipboard.writeText(window.location.origin + data.invite_link).catch(() => {});
      setEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="overline mb-3">{t("team.overline")}</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-10">{t("team.title")}</h1>

      <form onSubmit={invite} className="surface p-5 mb-6 flex gap-3 items-end" data-testid="team-invite-form">
        <div className="flex-1">
          <label className="overline block mb-1">{t("team.invite")}</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="team-invite-email" />
        </div>
        <div>
          <label className="overline block mb-1">{t("team.role")}</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}
            className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="team-invite-role">
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button className="btn-primary" data-testid="team-invite-submit"><Mail className="w-4 h-4" /> {t("team.send")}</button>
      </form>

      <div className="surface mb-6">
        <div className="overline p-5 pb-2">{t("team.active")}</div>
        <table className="w-full text-sm">
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-t border-white/5">
                <td className="p-4">{m.name}</td>
                <td className="p-4 font-mono text-xs text-white/60">{m.email}</td>
                <td className="p-4"><span className="px-2 py-0.5 bg-white/5 font-mono text-xs">{m.role}</span></td>
                <td className="p-4 font-mono text-xs text-white/40">{new Date(m.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invites.length > 0 && (
        <div className="surface">
          <div className="overline p-5 pb-2">{t("team.pending")}</div>
          <table className="w-full text-sm">
            <tbody>
              {invites.map((i) => (
                <tr key={i.id} className="border-t border-white/5">
                  <td className="p-4 font-mono text-xs">{i.email}</td>
                  <td className="p-4"><span className="px-2 py-0.5 bg-white/5 font-mono text-xs">{i.role}</span></td>
                  <td className="p-4">
                    <button onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + "/accept-invite/" + i.token);
                      toast.success("Invite link copied");
                    }} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                      <Copy className="w-3 h-3" /> {t("team.copyLink")}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={async () => { await api.delete(`/team/invites/${i.id}`); load(); }}
                      className="text-xs text-red-400 hover:underline inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> {t("common.revoke").toLowerCase()}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
